// Helper utility functions

// Generate unique ID
export const generateId = () => {
  return Date.now().toString(36) + Math.random().toString(36).substr(2);
};

// Format currency with symbol
export const formatCurrency = (amount, currency = '₹') => {
  const absAmount = Math.abs(amount);
  const formatted = new Intl.NumberFormat('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(absAmount);
  return `${amount < 0 ? '-' : ''}${currency}${formatted}`;
};

// Format date for display
export const formatDate = (dateString) => {
  const date = new Date(dateString);
  return new Intl.DateTimeFormat('en-IN', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  }).format(date);
};

// Format date for input fields
export const formatDateForInput = (dateString) => {
  const date = new Date(dateString);
  return date.toISOString().split('T')[0];
};

// Get relative time (e.g., "2 days ago")
export const getRelativeTime = (dateString) => {
  const date = new Date(dateString);
  const now = new Date();
  const diffInMs = now - date;
  const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));
  
  if (diffInDays === 0) return 'Today';
  if (diffInDays === 1) return 'Yesterday';
  if (diffInDays < 7) return `${diffInDays} days ago`;
  if (diffInDays < 30) return `${Math.floor(diffInDays / 7)} weeks ago`;
  if (diffInDays < 365) return `${Math.floor(diffInDays / 30)} months ago`;
  return `${Math.floor(diffInDays / 365)} years ago`;
};

// Calculate current balance (Overall and per Mode)
// Calculate current balance (Overall and per Mode)
export const calculateBalance = (transactions = [], lendings = [], borrowings = []) => {
  const transactionTotal = transactions.reduce((acc, t) => {
    if (t.type === 'income') return acc + t.amount;
    if (t.type === 'expense') return acc - t.amount;
    return acc;
  }, 0);
  
  const netLendingFlow = lendings.reduce((acc, l) => {
    const lentOut = l.amount;
    const settledIn = (l.settlements || []).reduce((s, item) => s + item.amount, 0);
    return acc - lentOut + settledIn;
  }, 0);
  
  const netBorrowingFlow = borrowings.reduce((acc, b) => {
    const borrowedIn = b.amount;
    const repaidOut = (b.settlements || []).reduce((s, item) => s + item.amount, 0);
    return acc + borrowedIn - repaidOut;
  }, 0);
  
  const outstandingLent = lendings.reduce((acc, l) => {
    const repaid = (l.settlements || []).reduce((s, item) => s + item.amount, 0);
    return acc + (l.isSettled ? 0 : Math.max(0, l.amount - repaid));
  }, 0);
  
  const outstandingBorrowed = borrowings.reduce((acc, b) => {
    const repaid = (b.settlements || []).reduce((s, item) => s + item.amount, 0);
    return acc + (b.isSettled ? 0 : Math.max(0, b.amount - repaid));
  }, 0);

  return {
    available: transactionTotal + netLendingFlow + netBorrowingFlow,
    actual: transactionTotal,
    lentOut: outstandingLent,
    borrowed: outstandingBorrowed,
  };
};

export const calculateGlobalStats = (state) => {
  const { transactions = [], lendings = [], borrowings = [], modes = [] } = state;

  const getModeBalance = (modeId) => {
    const txBalance = transactions.reduce((acc, t) => {
      if (t.type === 'income' && t.modeId === modeId) return acc + t.amount;
      if (t.type === 'expense' && t.modeId === modeId) return acc - t.amount;
      if (t.type === 'transfer') {
        if (t.modeId === modeId) return acc - t.amount;
        if (t.targetModeId === modeId) return acc + t.amount;
      }
      return acc;
    }, 0);
    
    const lendingSettlements = lendings.reduce((acc, l) => {
       return acc + (l.settlements || []).filter(s => s.modeId === modeId).reduce((sAcc, s) => sAcc + s.amount, 0);
    }, 0);
    
    const borrowingSettlements = borrowings.reduce((acc, b) => {
       return acc - (b.settlements || []).filter(s => s.modeId === modeId).reduce((sAcc, s) => sAcc + s.amount, 0);
    }, 0);
    
    const moneyLent = lendings.reduce((acc, l) => {
        if (l.modeId === modeId) return acc + l.amount;
        return acc;
    }, 0);

    const moneyBorrowed = borrowings.reduce((acc, b) => {
        if (b.modeId === modeId) return acc + b.amount;
        return acc;
    }, 0);
    
    return txBalance + lendingSettlements + borrowingSettlements - moneyLent + moneyBorrowed;
  };

  const totalLiquid = modes.reduce((acc, mode) => {
    const rawBalance = getModeBalance(mode.id);
    const rate = parseFloat(mode.exchangeRate) || 1;
    return acc + (rawBalance * rate);
  }, 0);

  const totalLent = lendings.reduce((acc, l) => {
    if (l.isSettled) return acc;
    const mode = modes.find(m => m.id === l.modeId);
    const rate = parseFloat(mode?.exchangeRate) || 1;
    const pending = l.amount - (l.settlements || []).reduce((s, x) => s + x.amount, 0);
    return acc + (pending * rate);
  }, 0);

  const totalBorrowed = borrowings.reduce((acc, b) => {
    if (b.isSettled) return acc;
    const mode = modes.find(m => m.id === b.modeId);
    const rate = parseFloat(mode?.exchangeRate) || 1;
    const pending = b.amount - (b.settlements || []).reduce((s, x) => s + x.amount, 0);
    return acc + (pending * rate);
  }, 0);

  return {
    netWorth: totalLiquid, // Available Liquid Cash (Base Currency)
    totalLent,
    totalBorrowed,
    getModeBalance // Helper for individual mode display
  };
};

// Get total income
export const getTotalIncome = (transactions) => {
  return transactions
    .filter(t => t.type === 'income')
    .reduce((acc, t) => acc + t.amount, 0);
};

// Get total expenses
export const getTotalExpenses = (transactions) => {
  return transactions
    .filter(t => t.type === 'expense')
    .reduce((acc, t) => acc + t.amount, 0);
};

// Get spending by category
export const getSpendingByCategory = (transactions, categories) => {
  const expenses = transactions.filter(t => t.type === 'expense');
  
  return categories.map(cat => {
    const total = expenses
      .filter(t => t.categoryId === cat.id)
      .reduce((acc, t) => acc + t.amount, 0);
    return {
      ...cat,
      total,
    };
  }).filter(c => c.total > 0).sort((a, b) => b.total - a.total);
};

// Get transactions for a specific month
export const getTransactionsForMonth = (transactions, year, month) => {
  return transactions.filter(t => {
    const date = new Date(t.date);
    return date.getFullYear() === year && date.getMonth() === month;
  });
};

// Get monthly summary
export const getMonthlySummary = (transactions, months = 6) => {
  const summary = [];
  const now = new Date();
  
  for (let i = months - 1; i >= 0; i--) {
    const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const monthTransactions = getTransactionsForMonth(
      transactions,
      date.getFullYear(),
      date.getMonth()
    );
    
    summary.push({
      month: date.toLocaleDateString('en-US', { month: 'short' }),
      year: date.getFullYear(),
      income: getTotalIncome(monthTransactions),
      expenses: getTotalExpenses(monthTransactions),
    });
  }
  
  return summary;
};

// Check and process recurring transactions
export const processRecurringTransactions = (recurringTransactions, lastCheck) => {
  const now = new Date();
  const newTransactions = [];
  const updatedRecurring = [];
  
  recurringTransactions.forEach(recurring => {
    if (!recurring.isActive) {
      updatedRecurring.push(recurring);
      return;
    }
    
    let nextOccurrence = new Date(recurring.nextOccurrence);
    
    while (nextOccurrence <= now) {
      // Create a new transaction
      newTransactions.push({
        id: generateId(),
        type: recurring.type,
        amount: recurring.amount,
        description: recurring.description,
        categoryId: recurring.categoryId,
        modeId: recurring.modeId, // Inherit mode
        targetModeId: recurring.targetModeId, // Inherit target mode for transfers
        tags: recurring.tags || [],
        date: nextOccurrence.toISOString(),
        createdAt: new Date().toISOString(),
        isFromRecurring: true,
        recurringId: recurring.id,
      });
      
      // Calculate next occurrence
      switch (recurring.frequency) {
        case 'daily':
          nextOccurrence.setDate(nextOccurrence.getDate() + 1);
          break;
        case 'weekly':
          nextOccurrence.setDate(nextOccurrence.getDate() + 7);
          break;
        case 'monthly':
          nextOccurrence.setMonth(nextOccurrence.getMonth() + 1);
          break;
        case 'yearly':
          nextOccurrence.setFullYear(nextOccurrence.getFullYear() + 1);
          break;
        default:
          break;
      }
    }
    
    updatedRecurring.push({
      ...recurring,
      nextOccurrence: nextOccurrence.toISOString(),
    });
  });
  
  return { newTransactions, updatedRecurring };
};

// Group transactions by date
export const groupTransactionsByDate = (transactions) => {
  const groups = {};
  
  transactions.forEach(t => {
    const date = new Date(t.date).toISOString().split('T')[0];
    if (!groups[date]) {
      groups[date] = [];
    }
    groups[date].push(t);
  });
  
  return Object.entries(groups)
    .sort(([a], [b]) => new Date(b) - new Date(a))
    .map(([date, items]) => ({
      date,
      transactions: items.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)),
    }));
};
